import React, { useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutSide";
import { icArrowDown2 } from "../image/image-src";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type Option<T> = { value: T; label: string };

export const CustomDropdown = <T,>({
  disabled,
  options,
  value,
  onChange,
  className,
  placeholder = "Select an option",
}: {
  disabled?: boolean;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  className: string;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useClickOutside({
    ref,
    onOutsideClick: () => setOpen(false),
  });
  const label = options.find((o) => o.value === value)?.label;
  const { t } = useTranslation();

  return (
    <div className="relative w-full">
      <button
        onClick={() => setOpen((o) => !o)}
        type="button"
        disabled={disabled}
        className={clsx(
          "flex items-center justify-between text-sm font-semibold sm:text-base sm:leading-[1.625rem]",
          className,
        )}
      >
        {label ?? <span className="text-gray-500">{t(placeholder)}</span>}
        <img
          className="h-[0.8125rem] w-[0.8125rem]"
          src={icArrowDown2}
          alt=""
        />
      </button>
      {open && (
        <div
          ref={ref}
          className="absolute right-0 z-20 mt-2 min-w-32 overflow-hidden rounded-xl border border-gray-200 bg-white py-3 shadow-lg"
        >
          <ul>
            {options.map((option) => (
              <li
                key={String(option.value)}
                className={twMerge(
                  "flex min-h-10 cursor-pointer items-center px-4 transition-colors duration-150",
                  "hover:bg-blue-50",
                  value === option.value ? "bg-blue-100 font-semibold" : "",
                )}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
